import Card from '@mui/material/Card'
import Typography from '@mui/material/Typography'
import IconButton from '@mui/material/IconButton'
import Box from '@mui/material/Box'
import { styled } from '@mui/material/styles'
import useMediaQuery from '@mui/material/useMediaQuery'
import { useTheme } from '@mui/material/styles'
import { sortBy } from 'lodash-es'
import { ArrowRight } from 'components/common/SVGElements/ArrowRight'
import NowrapDiv from 'components/common/NowrapDiv'
import { GtmAgent } from 'utils/hooks/analytics'
import { useRouter } from 'next/router'

const DEFAULT_SERVICELOGO = '/img/default_service_icon.svg'

const StyledCard = styled(Card)`
  ${({ theme }) => `
    position: relative;
    padding: 12px;
    display: flex;
    justify-content: space-between;
    gap: 12px;
    border: 1px solid ${theme.palette.grey.secondary};
    border-radius: 6px;
    background: ${theme.palette.white.main};
    cursor: pointer;
    &:hover,&:active {
      background: ${theme.palette.primary.bgPlus2};
    }
  `}
`

const IconButtonWrapper = styled(IconButton)`
  ${({ theme }) => `
  padding: 0;
    > svg {
      fill: ${theme.palette.text.secondary};
      width: 16px;
      height: 16px;
    }
  `}
`

const ServiceCard = ({ title, content, link, onClick, ...cardProps }) => (
  <StyledCard
    elevation={0}
    onClick={() => onClick && onClick({ title, content, link, ...cardProps })}
  >
    <Box display="flex" flexDirection="column" flex={1} minWidth={0} gap={1}>
      <Typography variant="labelLarge" color="text.minus2">
        <NowrapDiv $lines={2}>{title}</NowrapDiv>
      </Typography>
      {content && (
        <Typography variant="paraBodyXs" color="text.secondary">
          <NowrapDiv $lines={2}>{content}</NowrapDiv>
        </Typography>
      )}
    </Box>
    <IconButtonWrapper>
      <ArrowRight width="16" height="16" />
    </IconButtonWrapper>
  </StyledCard>
)

const ServiceCardGrid = ({ cards, onCardClick, tab, onSubMenusLinkClick }) => {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'))
  const router = useRouter()
  const handleCardClick = (card) => {
    GtmAgent.directSendToGTM({
      event: 'living_action',
      living_event_name: 'servicemenu_click',
      living_click_name: card?.title,
      ...(card?._id && {
        living_click_id: card._id,
      }),
      living_category1_name: tab?.label,
      living_category1_id: tab?._id,
    })

    if (onCardClick) {
      onCardClick(card.link)
      return
    }

    if (!card.link) return

    if (card.type === 'serviceClassification') {
      router.push(`/vender?serviceItem=${card?._id}`)
      return
    }

    if (card.linkTarget === 'blank') {
      window.open(card.link, '_blank')
    } else {
      router.push(card.link)
    }
  }

  return (
    <Box
      display="grid"
      gridTemplateColumns={isSmall ? 'repeat(2, 1fr)' : 'repeat(5, 1fr)'}
      gap={isSmall ? 2 : 4}
      mb={isSmall ? 12 : 18}
    >
      {tab?.link && (
        <Box gridColumn={isSmall ? '1 / -1' : 'span 2'}>
          <StyledCard
            elevation={0}
            onClick={onSubMenusLinkClick}
            sx={{
              alignItems: 'center',
              height: '100%',
              border: `1px solid ${theme.palette.primary.minus2}`,
              gap: 3,
              '&:hover, &:active': {
                border: `1px solid ${theme.palette.primary.main}`,
              },
            }}
          >
            <Box
              display="flex"
              alignItems="center"
              flex={1}
              minWidth={0}
              gap={3}
            >
              <Box
                display="flex"
                alignItems="center"
                justifyContent="center"
                width={44}
                height={44}
                flexShrink={0}
                borderRadius={1}
                sx={{
                  backgroundColor: 'grey.third',
                  '.MuiCard-root:hover &, .MuiCard-root:active &': {
                    backgroundColor: 'background.default',
                  },
                }}
              >
                <Box
                  component="img"
                  src={tab?.imageUrl || DEFAULT_SERVICELOGO}
                  width={24}
                  height={24}
                  sx={{
                    filter:
                      'brightness(0) saturate(100%) invert(35%) sepia(2%) saturate(3%) hue-rotate(22deg) brightness(92%) contrast(85%)',
                  }}
                />
              </Box>
              <Typography variant="titleLarge" color="text.minus2">
                <NowrapDiv $lines={2}>
                  {tab?.linkDisplayName || `前往${tab?.label}`}
                </NowrapDiv>
              </Typography>
            </Box>
            <IconButtonWrapper>
              <ArrowRight width="16" height="16" />
            </IconButtonWrapper>
          </StyledCard>
        </Box>
      )}
      {cards.map((card, idx) => (
        <ServiceCard key={idx} {...card} onClick={handleCardClick} />
      ))}
    </Box>
  )
}

export default function ServiceMenusCard({
  subcatalogs,
  onCardClick,
  tab,
  onSubMenusLinkClick,
}) {
  const theme = useTheme()
  const isSmall = useMediaQuery(theme.breakpoints.down('lg'))
  if (!subcatalogs || !Array.isArray(subcatalogs)) {
    return <ServiceCardGrid cards={[]} onCardClick={onCardClick} tab={tab} />
  }

  const allCards = subcatalogs.flatMap(
    (subcat) =>
      subcat.items
        ?.map((item) => {
          if (
            item.type === 'serviceClassification' &&
            item.serviceClassification
          ) {
            return {
              title: item.serviceClassification.name || '',
              content: item.serviceClassification.briefly || '',
              link: `/service/${item.serviceClassification.identityCode}`,
              type: 'serviceClassification',
              _id: item.serviceClassification._id,
              createdAt: item.serviceClassification.createdAt,
              venderCountByServiceItem:
                item.serviceClassification.venderCountByServiceItem || 0,
            }
          } else if (item.type === 'link') {
            return {
              title: item.name || '',
              content: item.description || '',
              link: item.link || '',
              linkTarget: item.linkTarget || 'self',
              type: 'link',
              sort: subcat.sort || 0,
            }
          }
          return null
        })
        .filter(Boolean) || []
  )

  // 去除重複的 serviceClassification（根據 _id）
  const uniqueCards = allCards.filter((card, index, array) => {
    if (card.type !== 'serviceClassification') {
      return true
    }

    // 對於 serviceClassification，只保留第一次出現的 _id
    return (
      array.findIndex(
        (c) => c.type === 'serviceClassification' && c._id === card._id
      ) === index
    )
  })

  const sortedCards = sortBy(uniqueCards, [
    // 首先按類型排序：serviceClassification 在前，link 在後
    (card) => (card.type === 'serviceClassification' ? 0 : 1),
    // 按 sort 排序（數字大的在前）
    (card) => (card.type === 'link' ? -card.sort : 0),
    // 按 venderCountByServiceItem 排序（數字大的在前）
    (card) =>
      card.type === 'serviceClassification'
        ? -card.venderCountByServiceItem
        : 0,
    // 按 createdAt 排序（新到舊）
    (card) =>
      card.type === 'serviceClassification'
        ? -new Date(card.createdAt || 0).getTime()
        : 0,
  ])

  const hasTabLinkCard = tab?.link
  const maxItems = isSmall
    ? hasTabLinkCard
      ? 8
      : 10
    : hasTabLinkCard
    ? 18
    : 20
  const cards = sortedCards.slice(0, maxItems)

  return (
    <ServiceCardGrid
      cards={cards}
      onCardClick={onCardClick}
      tab={tab}
      onSubMenusLinkClick={onSubMenusLinkClick}
    />
  )
}
